import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Play, ExternalLink, Users } from "lucide-react";
import { toast } from "sonner";
import TournamentForm, { tournamentToForm } from "@/components/TournamentForm";
import { TOURNAMENT_STATUS } from "@/pages/Tournaments";

export default function AdminTournaments({ onChanged }) {
  const [list, setList] = useState([]);
  const [dialog, setDialog] = useState(null); // null | "new" | tournament

  const load = useCallback(() => { api.get("/tournaments").then(r => setList(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  const done = () => { load(); onChanged?.(); };

  const confirm = async (t) => {
    try { await api.post(`/tournaments/${t._id}/confirm`); toast.success("Confirmed — order of play published"); done(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (t) => {
    if (!window.confirm(`Delete "${t.name}"?`)) return;
    try { await api.delete(`/tournaments/${t._id}`); toast.success("Deleted"); done(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Card className="border-border/50" data-testid="admin-tournaments">
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <CardTitle className="text-lg">Tournaments ({list.length})</CardTitle>
        <Button size="sm" onClick={() => setDialog("new")} className="rounded-full bg-coral hover:bg-coral-600 text-white gap-1" data-testid="admin-new-tournament"><Plus size={14} /> New</Button>
      </CardHeader>
      <CardContent className="space-y-2">
        {list.map(t => {
          const st = TOURNAMENT_STATUS[t.status] || TOURNAMENT_STATUS.open;
          const paid = (t.teams || []).filter(x => x.payment_status === "paid").length;
          return (
            <div key={t._id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 rounded-xl bg-muted/20 border border-border/30" data-testid="admin-tournament-row">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-heading font-bold text-sm">{t.name}</p>
                  <Badge className={`rounded-full text-[10px] ${st.cls}`}>{st.label}</Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">{t.date} · {t.start_time}–{t.end_time} · Rs {t.price_per_person}/person{t.prize ? ` · ${t.prize}` : ""}</p>
                <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5"><Users size={11} /> {t.teams?.length || 0}/{t.max_teams} teams · {paid} paid</p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {t.status === "open" && <Button size="sm" onClick={() => confirm(t)} disabled={(t.teams?.length || 0) < 2} className="rounded-full h-7 text-xs bg-golden hover:bg-golden-500 text-midnight-500 gap-1" data-testid={`admin-confirm-${t._id}`}><Play size={11} /> Confirm</Button>}
                <Link to={`/tournaments/${t._id}`}><Button size="icon" variant="ghost" className="w-7 h-7 rounded-full" title="Open"><ExternalLink size={12} /></Button></Link>
                <Button size="icon" variant="ghost" className="w-7 h-7 rounded-full" onClick={() => setDialog(t)} data-testid={`admin-edit-${t._id}`}><Pencil size={12} /></Button>
                <Button size="icon" variant="ghost" className="w-7 h-7 rounded-full text-destructive" onClick={() => remove(t)} data-testid={`admin-delete-${t._id}`}><Trash2 size={12} /></Button>
              </div>
            </div>
          );
        })}
        {list.length === 0 && <p className="text-sm text-muted-foreground text-center py-10">No tournaments yet — create your first event.</p>}
      </CardContent>

      <Dialog open={!!dialog} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{dialog === "new" ? "Organize a tournament" : "Edit tournament"}</DialogTitle><DialogDescription>Half-day event on Central Court, teams of two.</DialogDescription></DialogHeader>
          {dialog && <TournamentForm key={dialog === "new" ? "new" : dialog._id} initial={dialog === "new" ? undefined : tournamentToForm(dialog)} tournamentId={dialog === "new" ? undefined : dialog._id} onSaved={() => { setDialog(null); done(); }} />}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
